/*!
 * maker-related.js
 * Dynamic "Build with this board" + "Ask the community" sections.
 *
 * Learn posts:  fetched live from learn.circuit.rocks WP REST
 *               using product title/tags as search query (CORS open).
 * Forum threads: fetched from assets/forum-related.json (built by
 *                tools/build-forum-related.py from cr-bridge API).
 *                Product tags drive tag-bucket lookup; falls back to
 *                "default" bucket when no tag matches.
 *
 * SSR fallback: existing static blocks in the section render server-side.
 * JS only REPLACES them if it successfully fetches replacements. On any
 * error or empty result the static fallback stays visible.
 */
(function () {
  'use strict';

  // WP REST endpoint per source — section supplies it via data-wp-api.
  // Falls back to learn.circuit.rocks for backwards compatibility.
  var DEFAULT_WP_API = 'https://learn.circuit.rocks/wp-json/wp/v2/posts';
  // Forum JSON is theme-asset; URL injected by section via data-forum-json.

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function stripHtml(s) {
    var d = document.createElement('div');
    d.innerHTML = String(s || '');
    return (d.textContent || d.innerText || '').trim();
  }
  function truncate(s, n) {
    s = String(s || '').trim();
    if (s.length <= n) return s;
    return s.slice(0, n - 1).replace(/\s+\S*$/, '') + '…';
  }
  function fmtDate(iso) {
    if (!iso) return '';
    return String(iso).slice(0, 10);
  }

  function pickKeywords(title, tags) {
    var clean = String(title || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var STOP = { 'a':1,'an':1,'the':1,'with':1,'and':1,'for':1,'of':1,'to':1,'in':1,'on':1,'by':1,'or':1,'pack':1,'set':1,'kit':1,'pcs':1,'pieces':1,'module':1,'board':1,'sensor':1 };
    var words = clean.split(' ').filter(function (w) { return w && !STOP[w] && w.length > 1; });
    var primary = words.slice(0, 3).join(' ');
    if (primary) return primary;
    if (tags && tags.length) return tags[0];
    return '';
  }

  function fetchWp(apiBase, keywords) {
    var url = apiBase + '?per_page=4&_embed=wp:featuredmedia&_fields=id,date,link,title,excerpt,_links,_embedded';
    if (keywords) url += '&search=' + encodeURIComponent(keywords);
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('wp ' + r.status); return r.json(); });
  }

  function fetchForumJson(url) {
    return fetch(url, { mode: 'same-origin', credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('forum-json ' + r.status); return r.json(); });
  }

  function wpPostExcerpt(p) {
    var ex = p && p.excerpt && p.excerpt.rendered ? stripHtml(p.excerpt.rendered) : '';
    return truncate(ex, 140);
  }
  function wpPostImage(p) {
    try {
      var emb = p._embedded && p._embedded['wp:featuredmedia'];
      if (emb && emb[0]) {
        var sizes = emb[0].media_details && emb[0].media_details.sizes;
        if (sizes) {
          if (sizes.medium && sizes.medium.source_url) return sizes.medium.source_url;
          if (sizes.thumbnail && sizes.thumbnail.source_url) return sizes.thumbnail.source_url;
        }
        if (emb[0].source_url) return emb[0].source_url;
      }
    } catch (e) {}
    return '';
  }

  function renderWpCard(p, opts) {
    opts = opts || {};
    var metaLabel = opts.metaLabel || 'tutorial';
    var ctaLabel = opts.ctaLabel || 'read tutorial';
    var title = stripHtml(p.title && p.title.rendered || '');
    var excerpt = wpPostExcerpt(p);
    var date = fmtDate(p.date);
    // Text-only card — no image cell (matches /collections style).
    return ''
      + '<article class="vb-post vb-post--bare">'
      +   '<div class="vb-post-body">'
      +     '<div class="vb-post-meta">// ' + esc(metaLabel) + ' · ' + esc(date) + '</div>'
      +     '<h3><a href="' + esc(p.link) + '" target="_blank" rel="noopener">' + esc(title) + '</a></h3>'
      +     (excerpt ? '<p>' + esc(excerpt) + '</p>' : '')
      +     '<a class="vb-post-cta" href="' + esc(p.link) + '" target="_blank" rel="noopener">' + esc(ctaLabel) + ' &rarr;</a>'
      +   '</div>'
      + '</article>';
  }

  function renderForumCard(t) {
    var title = String(t.title || '');
    return ''
      + '<article class="vb-post">'
      +   '<div class="vb-post-body">'
      +     '<div class="vb-post-meta">// thread · ' + esc(t.date) + '</div>'
      +     '<h3><a href="' + esc(t.url) + '" target="_blank" rel="noopener">' + esc(title) + '</a></h3>'
      +     '<a class="vb-post-cta" href="' + esc(t.url) + '" target="_blank" rel="noopener">view thread &rarr;</a>'
      +   '</div>'
      + '</article>';
  }

  function readCtx(section) {
    return {
      handle: section.getAttribute('data-product-handle') || '',
      title:  section.getAttribute('data-product-title')  || '',
      tags: (section.getAttribute('data-product-tags') || '')
              .toLowerCase().split('|').map(function (s) { return s.trim(); }).filter(Boolean),
      forumJsonUrl: section.getAttribute('data-forum-json') || '/assets/forum-related.json',
      wpApi:    section.getAttribute('data-wp-api')    || DEFAULT_WP_API,
      metaLabel: section.getAttribute('data-meta-label') || 'tutorial',
      ctaLabel:  section.getAttribute('data-cta-label')  || 'read tutorial',
    };
  }

  function hydrateWp(section) {
    var ctx = readCtx(section);
    var posts = $('.vb-posts', section);
    if (!posts) return;
    var opts = { metaLabel: ctx.metaLabel, ctaLabel: ctx.ctaLabel };
    var kw = pickKeywords(ctx.title, ctx.tags);
    fetchWp(ctx.wpApi, kw)
      .then(function (data) {
        if (!Array.isArray(data) || !data.length) {
          return fetchWp(ctx.wpApi, '');
        }
        return data;
      })
      .then(function (data) {
        if (!Array.isArray(data) || !data.length) return;
        var html = data.slice(0, 4).map(function (p) { return renderWpCard(p, opts); }).join('');
        posts.innerHTML = html;
        section.setAttribute('data-related-state', 'live');
      })
      .catch(function () {
        section.setAttribute('data-related-state', 'fallback');
      });
  }

  function pickForumBucket(json, tags) {
    if (!json || !json.tags) return null;
    for (var i = 0; i < tags.length; i++) {
      var key = tags[i];
      if (json.tags[key] && json.tags[key].length) return json.tags[key];
      // partial match: tag contains bucket key
      for (var k in json.tags) {
        if (key.indexOf(k) !== -1 && json.tags[k].length) return json.tags[k];
      }
    }
    return null;
  }

  function hydrateForum(section) {
    var ctx = readCtx(section);
    var posts = $('.vb-posts', section);
    if (!posts) return;
    fetchForumJson(ctx.forumJsonUrl)
      .then(function (json) {
        var items = pickForumBucket(json, ctx.tags) || json.default || [];
        if (!items.length) return;
        var html = items.slice(0, 4).map(renderForumCard).join('');
        posts.innerHTML = html;
        section.setAttribute('data-related-state', 'live');
      })
      .catch(function () {
        section.setAttribute('data-related-state', 'fallback');
      });
  }

  function init() {
    $$('[data-related-source="learn"]').forEach(hydrateWp);
    $$('[data-related-source="blog"]').forEach(hydrateWp);
    $$('[data-related-source="forum"]').forEach(hydrateForum);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
